import ReactECharts from 'echarts-for-react';
import { useEffect, useRef } from 'react';

type GrowChartProps = Omit<React.ComponentProps<typeof ReactECharts>, 'style' | 'ref'>;

/**
 * An ECharts chart that fills its card and follows it: it sits in `.pp-chart-grow` (base height, grows with a stretched
 * grid cell) and re-measures whenever that box changes size. Cells switch between half and full row, and neighbouring
 * cells change the row height, so relying on window-resize alone leaves a stale canvas (plot overlapping the legend,
 * clipped axis names). Use this instead of a fixed-height `<ReactECharts>` for any chart that lives in the kind grid.
 */
const GrowChart: React.FunctionComponent<GrowChartProps> = (props) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactECharts>(null);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    // Hand ECharts the measured size explicitly instead of letting it re-read the DOM: the box changes size when the
    // cell goes half ⇄ full row or its neighbours change the row height, and a stale canvas overflows the card.
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) chartRef.current?.getEchartsInstance()?.resize({ width, height });
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  return (
    <div className='pp-chart-grow' ref={boxRef}>
      {/* echarts-for-react merges a default `height: 300` under `style`, which beats `inset: 0` — size it explicitly. */}
      <ReactECharts {...props} ref={chartRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
    </div>
  );
};

export default GrowChart;
